import React from 'react';
import * as Sentry from '@sentry/react';

// A task list meeting an action it has no widget for used to THROW from render. React 19
// then unmounted the page, so a client holding a leftover "Activate Account" task
// (T000_01, which the API creates and this app has never had a widget for) saw
// "Task action T000_01 not supported" in place of their whole dashboard.
//
// The list now leaves such a task out and reports it instead.
//
// WHICH SIGNAL THIS CONSUMES: the thrown error was the only thing that made an unknown
// action visible. It blanked the page, but it is how the T000_01 fault of 2026-09-30 was
// found, and it pointed straight at a server-side state that should not exist. After this
// change the page renders and the task is simply absent, so the replacement signal is
// the Sentry issue "Task action <code> not supported". Watch it: a new entry means the
// API is handing out a task this app cannot show, and the person holding it is missing
// something they were meant to see.

// An object so a test can stub the call without spying on an ES module namespace.
export const taskReporting = {
  report(message, extra) {
    Sentry.captureMessage(message, { level: 'error', extra });
  },
};

export function partitionTasks(tasks, mapping) {
  const all = tasks || [];
  return {
    supported: all.filter((task) => Boolean(mapping[task.action])),
    unsupported: all.filter((task) => !mapping[task.action]),
  };
}

export function useSupportedTasks(tasks, mapping, listName) {
  const { supported, unsupported } = partitionTasks(tasks, mapping);

  // Report once per distinct set of unsupported tasks, not on every render.
  const key = unsupported.map((task) => `${task.id}:${task.action}`).join(',');
  React.useEffect(() => {
    unsupported.forEach((task) => {
      taskReporting.report(`Task action ${task.action} not supported`, {
        listName,
        taskId: task.id,
        state: task.state,
      });
    });
    // `key` stands for `unsupported`, which is a new array on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, listName]);

  return supported;
}
